import { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import toast from "react-hot-toast";

export function useDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/drivers?includeInactive=true");
      setDrivers(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load drivers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // payload may be a plain object (JSON) or a FormData instance (file uploads) —
  // axios sets the right Content-Type for either automatically.
  async function addDriver(payload) {
    const { data } = await api.post("/api/drivers", payload);
    setDrivers(prev => [...prev, data]);
    return data;
  }

  async function updateDriver(id, payload) {
    const { data } = await api.put(`/api/drivers/${id}`, payload);
    setDrivers(prev => prev.map(d => (d.id === id ? data : d)));
    return data;
  }

  async function removeDriver(id) {
    await api.delete(`/api/drivers/${id}`);
    setDrivers(prev => prev.filter(d => d.id !== id));
  }

  async function setDriverVehicle(driverId, vehicleId) {
    if (vehicleId) {
      await api.put(`/api/vehicles/${vehicleId}/driver`, { driverId });
    } else {
      const current = drivers.find(d => d.id === driverId);
      if (current?.assignedVehicleId) {
        await api.put(`/api/vehicles/${current.assignedVehicleId}/driver`, { driverId: null });
      }
    }
    await load();
  }

  async function deactivateDriver(id) { await api.post(`/api/drivers/${id}/deactivate`); await load(); }
  async function reactivateDriver(id) { await api.post(`/api/drivers/${id}/reactivate`); await load(); }

  async function setCredentials(id, { email, tempPassword, sendCredentialsEmail }) {
    const { data } = await api.post(`/api/drivers/${id}/credentials`, { email, tempPassword, sendCredentialsEmail });
    await load();
    return data;
  }
  async function resetPassword(id, sendCredentialsEmail) {
    const { data } = await api.post(`/api/drivers/${id}/credentials/reset-password`, { sendCredentialsEmail });
    return data; // { ok, tempPassword } — shown once to the owner
  }
  async function disableLogin(id)        { await api.post(`/api/drivers/${id}/credentials/disable-login`); await load(); }
  async function enableLogin(id)         { await api.post(`/api/drivers/${id}/credentials/enable-login`); await load(); }
  async function forcePasswordChange(id) { await api.post(`/api/drivers/${id}/credentials/force-password-change`); await load(); }
  async function lockAccount(id)         { await api.post(`/api/drivers/${id}/credentials/lock`); await load(); }
  async function unlockAccount(id)       { await api.post(`/api/drivers/${id}/credentials/unlock`); await load(); }

  async function updatePermissions(id, updates) {
    const { data } = await api.put(`/api/drivers/${id}/permissions`, updates);
    return data;
  }

  return {
    drivers, loading, addDriver, updateDriver, removeDriver, setDriverVehicle, reload: load,
    deactivateDriver, reactivateDriver, setCredentials, resetPassword,
    disableLogin, enableLogin, forcePasswordChange, lockAccount, unlockAccount, updatePermissions,
  };
}
